<script setup lang="ts">
const props = defineProps<{
  date?: string
}>()
</script>

<template>
  <Hero>
    <div class="newspaper">
      <div class="masthead h-3 v-1">
        <h1>
          <Icon name="estia:triquetra" class="rotate-180" />
          The Estian Bulletin
          <Icon name="estia:triquetra" />
        </h1>
        <div class="container space-equal center-text border-bottom">
          <span class="date">{{ props.date || 'Extra edition' }}</span>
          <span class="motto">For the People of the League</span>
          <span class="price">Price: 5sp</span>
        </div>
      </div>
      <slot></slot>
    </div>
  </Hero>
</template>

<style lang="postcss">
.newspaper {
  padding: 1rem;
  box-shadow: var(--card-shadow);

  font-size: 1rem;
  min-width: 800px;
  width: 800px;
  margin: 1rem auto 1rem;
  line-height: 1.1;

  background: rgb(202, 194, 164);
  color: #212;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: auto;
  grid-auto-flow: row dense;
  gap: 0.5rem 1rem;

  font-family: 'Leander', serif;

  .v-1 {
    grid-row: span 1;
  }

  .v-2 {
    grid-row: span 2;
  }

  .v-3 {
    grid-row: span 3;
  }

  .v-4 {
    grid-row: span 4;
  }

  .v-5 {
    grid-row: span 5;
  }

  .v-6 {
    grid-row: span 6;
  }

  .h-1 {
    grid-column: span 1;
  }

  .h-2 {
    grid-column: span 2;
  }

  .h-3 {
    grid-column: span 3;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: #101 !important;
    border-bottom: none;
    margin: 0;
    font-family: 'Proclamate', serif;
    line-height: 1;
    text-align: center;
    border: none !important;

    > a {
      color: inherit;
    }
  }

  p {
    color: #101;
    text-align: justify;
    margin-bottom: 0;

    + p {
      margin-top: 0.5rem;
    }

    + h2,
    + h3 {
      margin-top: 0.75rem;
    }

    + h4,
    + h5,
    + h6 {
      margin-top: 0.5rem;
    }
  }

  table {
    color: #101;
    width: 100%;
    margin: 0;
    margin-top: .25em;

    th {
      color: #101;

      padding: 0;
    }

    tr {
      background: transparent !important;

      td {
        padding: 0;
      }
    }
  }

  .runes,
  .elvish {
    line-height: inherit;
    text-shadow: none;
    letter-spacing: 0;
  }

  .center-text {
    text-align: center;

    p {
      text-align: inherit;
    }
  }

  strong {
    color: #000;
    font-weight: 700;
  }

  hr {
    border-color: #000;
    margin: 0.5rem;
    column-span: all;
  }

  .border {
    border: 3px solid black;
    padding: 0.5rem;
  }

  .border-bottom {
    border-bottom: 3px solid black;
    padding-bottom: 0.5rem;
  }

  .masthead {
    h1 {
      display: flex;
      flex-direction: row;
      justify-content: space-evenly;
      align-items: center;

      .icon {
        width: 0.85em;
        height: 0.85em;
        align-self: flex-start;
      }
    }

    .motto {
      flex: 1;
      font-style: italic;
      font-weight: bold;
    }

    .date {
      flex: 1;
    }

    .price {
      flex: 1;
    }
  }

  .placeholder {
    display: flex;
    flex-direction: column;

    &::after {
      --placeholder: #0004;
      flex: 1;
      content: '';
      width: 100%;
      display: block;
      background: repeating-linear-gradient(
        180deg,
        transparent 0,
        transparent 2px,
        var(--placeholder) 2px,
        var(--placeholder) calc(1em - 2px),
        transparent calc(1em - 2px),
        transparent 1lh
      );
      background-repeat: repeat-x;
    }
  }

  .container {
    display: flex;
    gap: 0.5rem 1rem;
    justify-content: flex-start;

    &.vertical {
      flex-direction: column;
    }

    &.center {
      justify-content: center;
      align-items: center;
    }

    &.nogap {
      gap: 0;
    }

    &.space-between {
      justify-content: space-between;
    }

    .c-1 {
      flex: 0 1 calc(1 / 12 * 100%);
    }

    .c-2 {
      flex: 0 1 calc(2 / 12 * 100%);
    }

    .c-3 {
      flex: 0 1 25%;
    }

    .c-4 {
      flex: 0 1 calc(4 / 12 * 100%);
    }

    .c-5 {
      flex: 0 1 calc(5 / 12 * 100%);
    }

    .c-6 {
      flex: 0 1 50%;
    }

    .c-7 {
      flex: 0 1 calc(7 / 12 * 100%);
    }

    .c-8 {
      flex: 0 1 calc(8 / 12 * 100%);
    }

    .c-9 {
      flex: 0 1 75%;
    }

    .c-10 {
      flex: 0 1 calc(10 / 12 * 100%);
    }

    .c-11 {
      flex: 0 1 calc(11 / 12 * 100%);
    }

    .c-12 {
      flex: 0 1 100%;
    }

    .c-flex {
      flex: 1;
    }
  }

  h1 {
    margin-top: 0.5rem;
    font-size: 60px;
    width: 100%;
  }

  h2 {
    font-size: 48px;
    column-span: all;
  }

  h3 {
    font-size: 36px;
    column-span: all;
  }

  h4 {
    font-size: 24px;
  }

  h5 {
    font-size: 20px;
  }

  h6 {
    display: block;
    float: none;
    font-size: 1rem;
  }

  .dropcap {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      + p::first-letter {
        font-size: 2.2em;
        float: left;
        margin-right: 0.075em;
        margin-top: 0.11em;
      }
    }
  }

  .columns-2 {
    column-gap: 1rem;
    column-count: 2;
  }

  .columns-3 {
    column-gap: 1rem;
    column-count: 3;
  }

  @media print {
    background: none;
    padding: 0.5rem;
    min-width: unset;
    width: 800px;
    max-width: 100%;
    margin: none;
  }
}
</style>
